import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTour } from "./TourContext";

// Top-center horizontal pill. Avoids the bottom-right live-chat bubble
// and the app header's right-side actions, and reads as "tour mode"
// while a tour is running.
const TourProgressWidget: React.FC = () => {
  const { state, tours, nextStep, previousStep, goToStep, skipTour } = useTour();
  const [tocOpen, setTocOpen] = useState(false);

  const { active, tourId, stepIndex } = state;
  if (!active || !tourId) return null;

  const def = tours[tourId];
  if (!def || def.steps.length === 0) return null;

  const total = def.steps.length;
  const current = def.steps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / total) * 100);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  const wrapperStyle: React.CSSProperties = {
    position: "fixed",
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9997,
    maxWidth: "min(720px, 94vw)",
    width: "max-content"
  };

  const pillStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: 999,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    padding: "0.5rem 0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    fontSize: "0.85rem",
    position: "relative",
    overflow: "hidden"
  };

  const progressTrack: React.CSSProperties = {
    position: "absolute",
    left: 0,
    bottom: 0,
    height: 3,
    width: "100%",
    background: "#e9ecef"
  };
  const progressFill: React.CSSProperties = {
    height: "100%",
    width: `${progress}%`,
    background: "#3f6ad8",
    transition: "width 200ms ease"
  };

  const btnBase: React.CSSProperties = {
    border: "none",
    borderRadius: 6,
    padding: "0.3rem 0.7rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap"
  };
  const primaryBtn: React.CSSProperties = { ...btnBase, background: "#3f6ad8", color: "#fff" };
  const secondaryBtn: React.CSSProperties = {
    ...btnBase,
    background: "transparent",
    color: "#495057",
    border: "1px solid #dee2e6"
  };

  const tocStyle: React.CSSProperties = {
    marginTop: 8,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    padding: "0.5rem 0.75rem",
    maxHeight: 260,
    overflowY: "auto"
  };

  return ReactDOM.createPortal(
    <div style={wrapperStyle} data-testid="tour-progress-widget">
      <div style={pillStyle}>
        <span
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#6c757d",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap"
          }}
        >
          Step {stepIndex + 1} / {total}
        </span>
        <span
          style={{
            color: "#dee2e6",
            fontSize: "0.9rem"
          }}
        >
          ·
        </span>
        <span
          style={{
            fontWeight: 600,
            color: "#212529",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 260
          }}
          title={current.title}
        >
          {current.title}
        </span>

        <div style={{ display: "flex", gap: "0.35rem", alignItems: "center", marginLeft: "0.4rem" }}>
          <button
            onClick={previousStep}
            disabled={isFirst}
            style={{ ...secondaryBtn, opacity: isFirst ? 0.4 : 1, cursor: isFirst ? "not-allowed" : "pointer" }}
          >
            ← Back
          </button>
          <button onClick={nextStep} style={primaryBtn}>
            {isLast ? "Finish" : "Next →"}
          </button>
          <button
            onClick={() => setTocOpen(o => !o)}
            style={secondaryBtn}
            aria-expanded={tocOpen}
          >
            {tocOpen ? "Hide steps" : "All steps"}
          </button>
          <button
            onClick={skipTour}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#adb5bd",
              fontSize: "1rem",
              padding: "0 0.25rem",
              lineHeight: 1
            }}
            aria-label="Exit tour"
            title="Exit tour"
          >
            ✕
          </button>
        </div>

        <div style={progressTrack}>
          <div style={progressFill} />
        </div>
      </div>

      {tocOpen && (
        <ol style={{ ...tocStyle, listStyle: "decimal", paddingLeft: "1.75rem", margin: 0 }}>
          {def.steps.map((s, i) => {
            const isCurrent = i === stepIndex;
            const isDone = i < stepIndex;
            return (
              <li
                key={i}
                onClick={() => {
                  goToStep(i);
                  setTocOpen(false);
                }}
                style={{
                  cursor: "pointer",
                  padding: "0.3rem 0.4rem",
                  margin: "0 -0.4rem",
                  borderRadius: 4,
                  background: isCurrent ? "#eef2ff" : "transparent",
                  color: isDone ? "#868e96" : "#212529",
                  fontWeight: isCurrent ? 600 : 400,
                  fontSize: "0.8rem"
                }}
              >
                {isDone ? "✓ " : ""}
                {s.title}
              </li>
            );
          })}
        </ol>
      )}
    </div>,
    document.body
  );
};

export default TourProgressWidget;
